/**
 * ConceptBrowser — browse and search the knowledge base concept index.
 * Shows a filterable list of concepts with frequency counts.
 * Clicking a concept drills down to show all pages containing it.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pagesApi from '../../api/pages-api';
import type { ConceptItem, ConceptPage } from '../../api/pages-api';

export function ConceptBrowser(): React.ReactElement {
  const navigate = useNavigate();
  const [concepts, setConcepts] = useState<ConceptItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
  const [conceptPages, setConceptPages] = useState<ConceptPage[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch concepts with debounced filter
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await pagesApi.listConcepts({
          q: filter || undefined,
          limit: 100,
        });
        setConcepts(response.data ?? []);
      } catch {
        setConcepts([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filter]);

  // Fetch pages for selected concept
  useEffect(() => {
    if (!selectedConcept) {
      setConceptPages([]);
      return;
    }

    let cancelled = false;

    async function load(): Promise<void> {
      setPagesLoading(true);
      try {
        const response = await pagesApi.getConceptPages(selectedConcept!);
        if (!cancelled) {
          setConceptPages(response.data ?? []);
        }
      } catch {
        if (!cancelled) {
          setConceptPages([]);
        }
      } finally {
        if (!cancelled) {
          setPagesLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [selectedConcept]);

  const handleConceptClick = useCallback((concept: string) => {
    setSelectedConcept(concept === selectedConcept ? null : concept);
  }, [selectedConcept]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b">
        <h2 className="text-lg font-semibold">Concepts</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Browse topics across the knowledge base
        </p>
      </div>

      {/* Filter input */}
      <div className="px-4 py-2 border-b">
        <input
          type="text"
          placeholder="Filter concepts..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-3 py-1.5 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Filter concepts"
        />
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <p className="px-4 py-3 text-sm text-muted-foreground">Loading concepts...</p>
        )}

        {!loading && concepts.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {filter ? 'No concepts match your filter.' : 'No concepts yet. Concepts are extracted as pages are processed.'}
          </p>
        )}

        {!loading && concepts.length > 0 && (
          <ul className="divide-y" role="list" aria-label="Concepts list">
            {concepts.map((concept) => (
              <li key={concept.name}>
                <button
                  onClick={() => handleConceptClick(concept.name)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-accent/50 transition-colors ${
                    selectedConcept === concept.name ? 'bg-accent/70' : ''
                  }`}
                  aria-expanded={selectedConcept === concept.name}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{concept.name}</span>
                    <span className="text-xs text-muted-foreground ml-2 shrink-0">
                      {concept.pageCount} page{concept.pageCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </button>

                {/* Drill-down: pages containing this concept */}
                {selectedConcept === concept.name && (
                  <div className="bg-accent/20 px-4 py-2 border-t">
                    {pagesLoading && (
                      <p className="text-xs text-muted-foreground py-1">Loading pages...</p>
                    )}
                    {!pagesLoading && conceptPages.length === 0 && (
                      <p className="text-xs text-muted-foreground py-1">No accessible pages found.</p>
                    )}
                    {!pagesLoading && conceptPages.length > 0 && (
                      <ul className="space-y-1" role="list">
                        {conceptPages.map((page) => (
                          <li key={page.pageId}>
                            <button
                              onClick={() => navigate(`/pages/${page.libraryId}/${page.pageId}`)}
                              className="w-full text-left rounded px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors"
                            >
                              <p className="font-medium truncate">{page.pageTitle}</p>
                              <p className="text-xs text-muted-foreground">{page.libraryName}</p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
